import { useCallback, useEffect, useState } from 'react';
import { useNotify } from '../components/notices';
import { SessionContext, useRepo, useSession, useSnapshot } from '../context';
import { errorText } from '../lib/download';
import { describeLast } from '../lib/log';
import { TournamentSession } from '../lib/session';
import { engineOptions } from '../lib/settings';
import { TAB_LABELS, TABS, hrefFor, type Tab } from '../lib/router';
import { CrosstableTab } from './CrosstableTab';
import { LogTab } from './LogTab';
import { PlayersTab } from './PlayersTab';
import { RoundsTab } from './RoundsTab';
import { SetupTab } from './SetupTab';
import { StandingsTab } from './StandingsTab';

type Load =
  | { readonly status: 'loading' }
  | { readonly status: 'missing' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly session: TournamentSession };

export function TournamentPage({ id, tab }: { id: string; tab: Tab }) {
  const repo = useRepo();
  const notify = useNotify();
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    repo.get(id).then(
      (record) => {
        if (cancelled) return;
        if (record === undefined) {
          setLoad({ status: 'missing' });
          return;
        }
        try {
          const session = new TournamentSession(record.events, repo, (e) =>
            notify(`Could not save the last change: ${errorText(e)}`, 'error'),
          );
          setLoad({ status: 'ready', session });
        } catch (e) {
          setLoad({ status: 'error', message: errorText(e) });
        }
      },
      (e: unknown) => {
        if (!cancelled) setLoad({ status: 'error', message: errorText(e) });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [repo, id, notify]);

  if (load.status === 'loading') return <p>Loading…</p>;
  if (load.status !== 'ready') {
    return (
      <div className="error-box" role="alert">
        <h1>Tournament not available</h1>
        <p>
          {load.status === 'missing'
            ? 'There is no tournament with this address in this browser.'
            : `It could not be opened: ${load.message}`}
        </p>
        <p>
          <a href={hrefFor({ name: 'list' })}>Back to the tournament list</a>
        </p>
      </div>
    );
  }
  return (
    <SessionContext.Provider value={load.session}>
      <TournamentView tab={tab} />
    </SessionContext.Provider>
  );
}

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLInputElement && !['checkbox', 'radio', 'button'].includes(target.type))
  );
}

function TournamentView({ tab }: { tab: Tab }) {
  const session = useSession();
  const { tournament: t, events } = useSnapshot();
  const notify = useNotify();
  const s = t.settings;
  const last = describeLast(events);

  useEffect(() => {
    document.title = `${TAB_LABELS[tab]} · ${s.name} · Chess TD`;
  }, [tab, s.name]);

  const undo = useCallback(() => {
    const text = describeLast(session.store.events);
    const removed = session.undo();
    notify(removed === null ? 'Nothing to undo.' : `Undone: ${text ?? removed.type}.`);
  }, [session, notify]);

  // Ctrl+Z / Cmd+Z undoes the last change, except while typing in a text field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'z') {
        if (isEditable(e.target)) return;
        e.preventDefault();
        undo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo]);

  const systemName = engineOptions().find((e) => e.id === s.system)?.name ?? s.system;
  const dates = [s.startDate, s.endDate].filter(Boolean).join(' to ');
  const meta = [dates, s.city, systemName, `round ${t.rounds.length} of ${s.rounds}`]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="tournament">
      <nav aria-label="Breadcrumb" className="breadcrumb no-print">
        <a href={hrefFor({ name: 'list' })}>Tournaments</a>
      </nav>
      <div className="t-header">
        <div>
          <h1 className="t-title">{s.name}</h1>
          <p className="meta">{meta}</p>
        </div>
        <div className="t-undo no-print">
          <button
            type="button"
            className="button"
            onClick={undo}
            disabled={last === null}
            aria-describedby="last-change"
            aria-keyshortcuts="Control+Z"
          >
            Undo
          </button>
          <span id="last-change" className="hint">
            {last === null ? 'No changes to undo' : `Last change: ${last}`}
          </span>
        </div>
      </div>
      <nav aria-label="Tournament sections" className="tabs no-print">
        <ul>
          {TABS.map((k) => (
            <li key={k}>
              <a
                href={hrefFor({ name: 'tournament', id: t.id, tab: k })}
                aria-current={k === tab ? 'page' : undefined}
              >
                {TAB_LABELS[k]}
              </a>
            </li>
          ))}
        </ul>
      </nav>
      <div className="tab-panel">
        {tab === 'setup' && <SetupTab />}
        {tab === 'players' && <PlayersTab />}
        {tab === 'rounds' && <RoundsTab />}
        {tab === 'standings' && <StandingsTab />}
        {tab === 'crosstable' && <CrosstableTab />}
        {tab === 'log' && <LogTab />}
      </div>
    </div>
  );
}
